import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { Cog, MapPin, Hash } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'operativa', label: 'Operativa', color: 'emerald' },
  { value: 'pendiente_repuesto', label: 'Esperando Repuesto', color: 'amber' },
  { value: 'en_reparacion', label: 'En Reparación', color: 'sky' },
  { value: 'fuera_servicio', label: 'Fuera de Servicio', color: 'rose' },
];

export default function ClientMachines() {
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchMachines();
  }, []);

  const fetchMachines = async () => {
    try {
      const response = await axios.get(`${API}/machines`);
      setMachines(response.data);
    } catch (error) {
      toast.error('Error al cargar máquinas');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    const colors = {
      emerald: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
      amber: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      sky: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
      rose: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    };
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-mono uppercase tracking-wider border ${colors[option?.color || 'emerald']}`}>
        {option?.label || status}
      </span>
    );
  };

  return (
    <Layout title="Mis Máquinas">
      <div className="space-y-6 animate-fade-in" data-testid="client-machines">
        <p className="text-slate-400">Estado actual de tus máquinas registradas</p>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : machines.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="py-12 text-center">
              <Cog className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No tienes máquinas registradas</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {machines.map((machine) => (
              <Card key={machine.id} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-all">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-rajdhani text-lg font-semibold text-white">{machine.name}</h3>
                      {machine.model && <p className="text-sm text-slate-500 font-mono">{machine.model}</p>}
                    </div>
                    <div className="w-10 h-10 rounded-full bg-slate-800 flex items-center justify-center">
                      <Cog className="w-5 h-5 text-sky-400" />
                    </div>
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    {machine.serial_number && (
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <Hash className="w-4 h-4 text-slate-500" />
                        <span className="font-mono">{machine.serial_number}</span>
                      </div>
                    )}
                    {machine.location && (
                      <div className="flex items-center gap-2 text-sm text-slate-400">
                        <MapPin className="w-4 h-4 text-slate-500" />
                        <span>{machine.location}</span>
                      </div>
                    )}
                  </div>
                  
                  {getStatusBadge(machine.status)}
                  
                  {machine.pending_parts && (
                    <div className="mt-3 p-3 bg-amber-500/5 border border-amber-500/20 rounded-sm">
                      <p className="text-xs text-amber-400 font-medium mb-1">Repuestos pendientes:</p>
                      <p className="text-sm text-slate-300">{machine.pending_parts}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
